import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { adminApi } from '../../api/client';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: '', name_nl: '', name_fr: '', name_en: '', sortOrder: 0 });

  useEffect(() => {
    adminApi.listCategories().then(r => setCategories(r.data)).finally(() => setLoading(false));
    // Mount-only: adminApi is a stable module-level import
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => { setForm({ slug: '', name_nl: '', name_fr: '', name_en: '', sortOrder: 0 }); setEditing(null); };

  const handleSave = async () => {
    if (!form.name_nl || !form.slug) return;
    if (editing) {
      const { data } = await adminApi.updateCategory(editing, form);
      setCategories(prev => prev.map(c => c._id === editing ? data : c));
    } else {
      const { data } = await adminApi.createCategory(form);
      setCategories(prev => [...prev, data]);
    }
    resetForm();
  };

  const startEdit = (cat) => {
    setEditing(cat._id);
    setForm({ slug: cat.slug, name_nl: cat.name_nl, name_fr: cat.name_fr || '', name_en: cat.name_en || '', sortOrder: cat.sortOrder || 0 });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Categorie verwijderen?')) return;
    await adminApi.deleteCategory(id);
    setCategories(prev => prev.filter(c => c._id !== id));
  };

  return (
    <div data-testid="admin-categories-page" className="p-8">
      <h1 className="font-serif text-3xl font-light text-foreground mb-8">Categorieen</h1>

      <div className="bg-card border border-border rounded-2xl p-6 mb-8 shadow-sm">
        <h2 className="text-sm font-semibold text-foreground mb-4">{editing ? 'Categorie bewerken' : 'Nieuwe categorie'}</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <input data-testid="cat-slug" placeholder="slug" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} className={inputClass} />
          <input data-testid="cat-name-nl" placeholder="Naam NL" value={form.name_nl} onChange={e => setForm(f => ({ ...f, name_nl: e.target.value }))} className={inputClass} />
          <input placeholder="Naam FR" value={form.name_fr} onChange={e => setForm(f => ({ ...f, name_fr: e.target.value }))} className={inputClass} />
          <input placeholder="Naam EN" value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} className={inputClass} />
          <input type="number" placeholder="Volgorde" value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))} className={inputClass} />
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="save-cat-btn" onClick={handleSave} className="bg-primary text-primary-foreground rounded-full px-6 py-2 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95">
            {editing ? 'Bijwerken' : 'Toevoegen'}
          </button>
          {editing && <button onClick={resetForm} className="px-6 py-2 border border-border rounded-full text-sm hover:bg-muted transition-colors">Annuleren</button>}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={`categories-skeleton-${i}`} className="h-12 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3.5 text-left font-semibold">Slug</th>
                <th className="px-5 py-3.5 text-left font-semibold">Naam NL</th>
                <th className="px-5 py-3.5 text-left font-semibold">Naam FR</th>
                <th className="px-5 py-3.5 text-left font-semibold">Naam EN</th>
                <th className="px-5 py-3.5 text-center font-semibold">Volgorde</th>
                <th className="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {categories.map(c => (
                <tr key={c._id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{c.slug}</td>
                  <td className="px-5 py-3 font-medium">{c.name_nl}</td>
                  <td className="px-5 py-3 text-muted-foreground">{c.name_fr || '--'}</td>
                  <td className="px-5 py-3 text-muted-foreground">{c.name_en || '--'}</td>
                  <td className="px-5 py-3 text-center">{c.sortOrder || 0}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => startEdit(c)} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(c._id)} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!categories.length && <p className="text-center py-12 text-muted-foreground text-sm">Geen categorieen</p>}
        </div>
      )}
    </div>
  );
}
